import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BookOpen,
  BookMarked,
  Plus,
  FileText,
  Clock,
  CheckCircle,
  Brain,
  Search,
  Settings,
  RotateCcw,
  XCircle,
  Loader2,
  X,
  Check,
  Filter,
  ChevronDown,
  Download,
  Trash2,
  FileEdit,
  Send,
} from "lucide-react";
import ROUTE_PATH from "../../constants/routes";
import {
  getHistoryStatistics,
  getHistoryDocuments,
  getHistorySubCategories,
  createHistoryDocument,
  getHistoryDocumentDetail,
  updateHistoryDocument,
  deleteHistoryDocument,
  updateHistoryDocumentStatus,
  approveHistoryDocument,
  rejectHistoryDocument,
  unapproveHistoryDocument,
  aiLearnHistoryDocument,
  exportHistoryDocuments,
  getLegalStatistics,
  getLegalDocuments,
  getLegalDocTypes,
  getLegalIssuingAgencies,
  getLegalDocumentDetail,
  createLegalDocument,
  updateLegalDocument,
  deleteLegalDocument,
  updateLegalDocumentStatus,
  approveLegalDocument,
  rejectLegalDocument,
  unapproveLegalDocument,
  aiLearnLegalDocument,
  exportLegalDocuments,
} from "../../apis/document";
import BaseTable from "../../components/base/BaseTable";
import BaseModal, { ConfirmModal } from "../../components/base/BaseModal";
import { formatDateShort } from "../../utils/formatDate";
import DocumentFormModal from "../../components/documents/DocumentFormModal";
import DocumentDetailModal from "../../components/documents/DocumentDetailModal";
import LegalDocumentFormModal from "../../components/documents/LegalDocumentFormModal";
import LegalDocumentDetailModal from "../../components/documents/LegalDocumentDetailModal";
import RejectDocumentModal from "../../components/documents/RejectDocumentModal";
import DeleteDocumentModal from "../../components/documents/DeleteDocumentModal";
import DeletedDocumentsModal from "../../components/documents/DeletedDocumentsModal";
import { showToast } from "../../utils/toastNotification";
import CustomSelect from "../../components/base/CustomSelect";
import { usePermission } from "../../hooks/usePermission";
import VietnameseDatePicker from "../../components/documents/VietnameseDatePicker";
import { useAuth } from "../../contexts/AuthContext";

const TABS = [
  {
    id: "history",
    label: "Văn hóa - Lịch sử",
    icon: BookOpen,
    path: ROUTE_PATH.DOCUMENTS_HISTORY,
  },
  {
    id: "legal",
    label: "Quy phạm pháp luật",
    icon: BookMarked,
    path: ROUTE_PATH.DOCUMENTS_LEGAL,
  },
];

const STAT_CARDS = [
  { key: "total", label: "Tổng tài liệu", icon: FileText, color: "blue" },
  { key: "pending", label: "Chờ duyệt", icon: Clock, color: "orange" },
  { key: "approved", label: "Đã duyệt", icon: CheckCircle, color: "green" },
  { key: "aiLearned", label: "AI đã học", icon: Brain, color: "purple" },
];

const COLOR_MAP = {
  blue: { bg: "bg-blue-50", icon: "text-blue-600", text: "text-blue-700" },
  orange: {
    bg: "bg-orange-50",
    icon: "text-orange-600",
    text: "text-orange-700",
  },
  green: { bg: "bg-green-50", icon: "text-green-600", text: "text-green-700" },
  purple: {
    bg: "bg-purple-50",
    icon: "text-purple-600",
    text: "text-purple-700",
  },
};

const ALL_HISTORY_COLUMN_KEYS = [
  "tieu_de",
  "di_tich",
  "phan_nhom",
  "ngay_ban_hanh",
  "pham_vi",
  "ai_da_hoc",
  "trang_thai",
  "ten_nguoi_tao",
  "ten_nguoi_duyet",
];

const ALL_LEGAL_COLUMN_KEYS = [
  "so_hieu",
  "tieu_de",
  "co_quan_ban_hanh",
  "loai_van_ban",
  "ngay_ban_hanh",
  "pham_vi",
  "ai_da_hoc",
  "trang_thai",
  "ten_nguoi_tao",
  "ten_nguoi_duyet",
];

const REPORT_PERIOD_OPTIONS = [
  { value: "", label: "Tất cả thời gian / Tùy chọn" },
  { value: "HOM_NAY", label: "Hôm nay" },
  { value: "TUAN_NAY", label: "Tuần này" },
  { value: "THANG_NAY", label: "Tháng này" },
  { value: "THANG_TRUOC", label: "Tháng trước" },
  { value: "QUY_NAY", label: "Quý này" },
  { value: "QUY_TRUOC", label: "Quý trước" },
  { value: "NAM_NAY", label: "Năm nay" },
  { value: "NAM_TRUOC", label: "Năm trước" },
];

const getPeriodDateRange = (period) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  const formatDate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  switch (period) {
    case "HOM_NAY": {
      const todayStr = formatDate(now);
      return { fromDate: todayStr, toDate: todayStr };
    }
    case "TUAN_NAY": {
      const day = now.getDay();
      const diffToMonday = day === 0 ? -6 : 1 - day;
      const monday = new Date(year, month, now.getDate() + diffToMonday);
      const sunday = new Date(year, month, monday.getDate() + 6);
      return { fromDate: formatDate(monday), toDate: formatDate(sunday) };
    }
    case "THANG_NAY": {
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      return { fromDate: formatDate(firstDay), toDate: formatDate(lastDay) };
    }
    case "THANG_TRUOC": {
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);
      return { fromDate: formatDate(firstDay), toDate: formatDate(lastDay) };
    }
    case "QUY_NAY": {
      const currentQuarter = Math.floor(month / 3);
      const firstDay = new Date(year, currentQuarter * 3, 1);
      const lastDay = new Date(year, (currentQuarter + 1) * 3, 0);
      return { fromDate: formatDate(firstDay), toDate: formatDate(lastDay) };
    }
    case "QUY_TRUOC": {
      const currentQuarter = Math.floor(month / 3);
      const firstDay = new Date(year, (currentQuarter - 1) * 3, 1);
      const lastDay = new Date(year, currentQuarter * 3, 0);
      return { fromDate: formatDate(firstDay), toDate: formatDate(lastDay) };
    }
    case "NAM_NAY": {
      const firstDay = new Date(year, 0, 1);
      const lastDay = new Date(year, 11, 31);
      return { fromDate: formatDate(firstDay), toDate: formatDate(lastDay) };
    }
    case "NAM_TRUOC": {
      const firstDay = new Date(year - 1, 0, 1);
      const lastDay = new Date(year - 1, 11, 31);
      return { fromDate: formatDate(firstDay), toDate: formatDate(lastDay) };
    }
    default:
      return { fromDate: "", toDate: "" };
  }
};

export default function DocumentsManagement() {
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [search, setSearch] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    trangThai: "",
    phamVi: "",
    danhMuc: "",
    agency: "",
    kyBaoCao: "",
    fromDate: "",
    toDate: "",
  });

  const [draftFilters, setDraftFilters] = useState({
    trangThai: "",
    phamVi: "",
    danhMuc: "",
    agency: "",
    kyBaoCao: "",
    fromDate: "",
    toDate: "",
  });

  const [formMode, setFormMode] = useState(null); // null | 'create' | 'edit'
  const [subCategories, setSubCategories] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [issuingAgencies, setIssuingAgencies] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Column Visibility Customization - Default is all columns
  const [visibleHistoryCols, setVisibleHistoryCols] = useState(ALL_HISTORY_COLUMN_KEYS);
  const [visibleLegalCols, setVisibleLegalCols] = useState(ALL_LEGAL_COLUMN_KEYS);

  const [showColumnSetting, setShowColumnSetting] = useState(false);
  const columnSettingRef = useRef(null);

  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterDropdownRef = useRef(null);

  const activeFilterCount = [
    appliedFilters.trangThai,
    appliedFilters.phamVi,
    appliedFilters.danhMuc,
    appliedFilters.agency,
    appliedFilters.kyBaoCao || appliedFilters.fromDate || appliedFilters.toDate,
  ].filter(Boolean).length;

  // View detail
  const [detailDocument, setDetailDocument] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Edit
  const [editDocument, setEditDocument] = useState(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  // Delete
  const [deleteDocument, setDeleteDocument] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletedModalOpen, setIsDeletedModalOpen] = useState(false);

  // Reject
  const [rejectDocument, setRejectDocument] = useState(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  // Unapprove
  const [unapproveDocument, setUnapproveDocument] = useState(null);
  const [isUnapproveConfirmOpen, setIsUnapproveConfirmOpen] = useState(false);
  const [isUnapproving, setIsUnapproving] = useState(false);

  // Approve loading
  const [approvingId, setApprovingId] = useState(null);

  // Export excel loading
  const [isExporting, setIsExporting] = useState(false);

  const { canDoAction, can } = usePermission();
  const { auth } = useAuth();
  const currentUser = auth?.user;

  const isCreatedByCurrentUser = (record) => {
    if (!record || !currentUser) return false;
    const currentUserId = currentUser.userId || currentUser.id;
    const currentUsername = currentUser.username || currentUser.tenDangNhap;

    if (
      currentUserId &&
      (record.nguoi_tao === currentUserId ||
        record.nguoi_tao_id === currentUserId ||
        record.id_nguoi_tao === currentUserId ||
        record.created_by === currentUserId ||
        record.nguoi_tao_info?.id === currentUserId)
    ) {
      return true;
    }

    if (
      currentUsername &&
      (record.nguoi_tao === currentUsername ||
        record.ten_nguoi_tao === currentUsername ||
        record.nguoi_tao_info?.ten_dang_nhap === currentUsername ||
        record.nguoi_tao_info?.username === currentUsername)
    ) {
      return true;
    }

    return false;
  };

  const canApproveDocument =
    can("TL_APPROVE") ||
    can("TL_DUYET") ||
    can("TL_PHE_DUYET") ||
    canDoAction("TL", "APPROVE") ||
    canDoAction("TL", "DUYET") ||
    canDoAction("TL", "PHE_DUYET");
  const canRejectDocument =
    can("TL_REJECT") ||
    can("TL_TU_CHOI") ||
    canDoAction("TL", "REJECT") ||
    canDoAction("TL", "TU_CHOI") ||
    canApproveDocument;
  const canUnapproveDocument =
    can("TL_UNAPPROVE") ||
    can("TL_HOAN_TAC") ||
    canDoAction("TL", "UNAPPROVE") ||
    canDoAction("TL", "HOAN_TAC") ||
    canApproveDocument;
  const canAdminDeleteDocument =
    can("TL_ADMIN_DELETE") ||
    canDoAction("TL", "ADMIN_DELETE");
  const canAiLearn = canDoAction("TL", "AI_LEARN") || can("TL_AI_LEARN") || can("TL_AI");
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [activeMenuRecord, setActiveMenuRecord] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, placement: "bottom" });
  const actionMenuRef = useRef(null);

  // Mở menu thao tác với vị trí viewport được tính toán chính xác
  const handleOpenActionMenu = (e, record) => {
    e.stopPropagation();
    if (openActionMenuId === record.id) {
      setOpenActionMenuId(null);
      setActiveMenuRecord(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom;

    if (spaceBelow < menuHeight && rect.top > menuHeight) {
      setMenuPosition({
        top: rect.top - 6,
        left: rect.right,
        placement: "top",
      });
    } else {
      setMenuPosition({
        top: rect.bottom + 6,
        left: rect.right,
        placement: "bottom",
      });
    }
    setOpenActionMenuId(record.id);
    setActiveMenuRecord(record);
  };

  // Đóng menu action khi click ra ngoài hoặc scroll
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setOpenActionMenuId(null);
        setActiveMenuRecord(null);
      }
      if (columnSettingRef.current && !columnSettingRef.current.contains(e.target)) {
        setShowColumnSetting(false);
      }
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setShowFilterDropdown(false);
      }
    };
    const handleScroll = () => {
      if (actionMenuRef.current) {
        setOpenActionMenuId(null);
        setActiveMenuRecord(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const handleToggleColumn = (colKey) => {
    if (activeTab === "history") {
      setVisibleHistoryCols((prev) => {
        const next = prev.includes(colKey)
          ? prev.filter((k) => k !== colKey)
          : [...prev, colKey];
        return next;
      });
    } else {
      setVisibleLegalCols((prev) => {
        const next = prev.includes(colKey)
          ? prev.filter((k) => k !== colKey)
          : [...prev, colKey];
        return next;
      });
    }
  };

  const handleResetColumns = () => {
    if (activeTab === "history") {
      setVisibleHistoryCols(ALL_HISTORY_COLUMN_KEYS);
    } else {
      setVisibleLegalCols(ALL_LEGAL_COLUMN_KEYS);
    }
  };

  const activeTab =
    location.pathname === ROUTE_PATH.DOCUMENTS_HISTORY
      ? "history"
      : location.pathname === ROUTE_PATH.DOCUMENTS_LEGAL
        ? "legal"
        : null;

  const currentTabRef = useRef(activeTab);
  currentTabRef.current = activeTab;
  const isInitialTabMountRef = useRef(true);

  useEffect(() => {
    if (!activeTab && TABS.length > 0) {
      navigate(TABS[0].path, { replace: true });
    }
  }, [activeTab, navigate]);

  const fetchHistoryStats = async (isBackground = false) => {
    if (!isBackground) setLoadingStats(true);
    try {
      const data = await getHistoryStatistics();
      if (data && currentTabRef.current === "history") {
        setStats(data);
      }
    } finally {
      if (!isBackground) {
        setLoadingStats(false);
      }
    }
  };

  const fetchLegalStats = async (isBackground = false) => {
    if (!isBackground) setLoadingStats(true);
    try {
      const data = await getLegalStatistics();
      if (data && currentTabRef.current === "legal") {
        setStats(data);
      }
    } finally {
      if (!isBackground) {
        setLoadingStats(false);
      }
    }
  };

  const fetchHistorySubCategoriesList = async () => {
    const data = await getHistorySubCategories();
    if (data && currentTabRef.current === "history") setSubCategories(data);
  };

  const fetchLegalTypesAndAgencies = async () => {
    const [types, agencies] = await Promise.all([
      getLegalDocTypes(),
      getLegalIssuingAgencies(),
    ]);
    if (currentTabRef.current === "legal") {
      if (types) setDocTypes(types);
      if (agencies) setIssuingAgencies(agencies);
    }
  };

  const fetchDocuments = useCallback(
    async (page = 1, pageSize = 10, customParams = {}, silent = false) => {
      const tab = activeTab;
      if (!tab) return;
      if (!silent) setLoadingTable(true);
      const params = {
        page,
        size: pageSize,
        search: (customParams.search !== undefined ? customParams.search : search).trim() || undefined,
      };
      const activeState = {
        ...appliedFilters,
        ...customParams,
      };

      if (activeState.trangThai) params.trangThai = activeState.trangThai;
      if (activeState.phamVi) params.phamVi = activeState.phamVi;
      if (activeState.danhMuc) params.idDanhMuc = activeState.danhMuc;
      if (activeState.agency) params.coQuanBanHanh = activeState.agency;
      if (activeState.aiDaHoc !== undefined && activeState.aiDaHoc !== "") {
        params.aiDaHoc = activeState.aiDaHoc;
        params.ai_da_hoc = activeState.aiDaHoc;
      }
      if (activeState.fromDate) {
        params.dateFrom = activeState.fromDate;
        params.fromDate = activeState.fromDate;
      }
      if (activeState.toDate) {
        params.dateTo = activeState.toDate;
        params.toDate = activeState.toDate;
      }

      try {
        const sortDocsByPriority = (docs) => {
          if (!Array.isArray(docs)) return [];
          return [...docs].sort((a, b) => {
            if (a.trang_thai === "TU_CHOI" && b.trang_thai !== "TU_CHOI") return -1;
            if (b.trang_thai === "TU_CHOI" && a.trang_thai !== "TU_CHOI") return 1;
            return 0;
          });
        };

        if (tab === "history") {
          const result = await getHistoryDocuments(params);
          if (currentTabRef.current === "history" && result) {
            setDocuments(sortDocsByPriority(result.data || []));
            setPagination(result.pagination || null);
          }
        } else if (tab === "legal") {
          const result = await getLegalDocuments(params);
          if (currentTabRef.current === "legal" && result) {
            setDocuments(sortDocsByPriority(result.data || []));
            setPagination(result.pagination || null);
          }
        }
      } catch (err) {
        console.error("Error fetching documents:", err);
      } finally {
        if (currentTabRef.current === tab) {
          setLoadingTable(false);
        }
      }
    },
    [activeTab, search, appliedFilters],
  );

  // Load data based on active tab
  useEffect(() => {
    if (!activeTab) return;

    const emptyFilters = {
      trangThai: "",
      phamVi: "",
      danhMuc: "",
      agency: "",
      kyBaoCao: "",
      fromDate: "",
      toDate: "",
      aiDaHoc: undefined,
    };

    // Reset table state immediately when switching tabs
    setDocuments([]);
    setPagination(null);
    setStats(null);
    setSearch("");
    setAppliedFilters(emptyFilters);
    setDraftFilters(emptyFilters);
    setShowFilterDropdown(false);
    setFormMode(null);
    setEditDocument(null);

    if (activeTab === "history") {
      fetchHistoryStats();
      fetchHistorySubCategoriesList();
    } else if (activeTab === "legal") {
      fetchLegalStats();
      fetchLegalTypesAndAgencies();
    }

    fetchDocuments(1, 10, {
      search: "",
      ...emptyFilters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const searchTimerRef = useRef(null);

  // Debounce search: tự động tìm kiếm sau 400ms khi người dùng gõ từ khóa
  useEffect(() => {
    if (isInitialTabMountRef.current) {
      isInitialTabMountRef.current = false;
      return;
    }
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      fetchDocuments(1);
    }, 400);
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handlePageChange = (page) => {
    fetchDocuments(page);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDocuments(1);
  };

  const handleToggleFilterDropdown = () => {
    if (!showFilterDropdown) {
      // Sync draft state with currently applied filters when opening
      setDraftFilters({ ...appliedFilters });
    }
    setShowFilterDropdown((prev) => !prev);
  };

  const handlePeriodChange = (period) => {
    if (!period) {
      setDraftFilters((prev) => ({
        ...prev,
        kyBaoCao: "",
        fromDate: "",
        toDate: "",
      }));
      return;
    }
    const { fromDate, toDate } = getPeriodDateRange(period);
    setDraftFilters((prev) => ({
      ...prev,
      kyBaoCao: period,
      fromDate,
      toDate,
    }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setShowFilterDropdown(false);
    fetchDocuments(1, 10, {
      ...draftFilters,
      search,
    });
  };

  // Click nhanh vào thẻ thống kê để lọc danh sách
  const handleStatCardClick = (cardKey) => {
    let newTrangThai = "";
    let newAiDaHoc = undefined;

    if (cardKey === "pending") {
      newTrangThai = appliedFilters.trangThai === "CHO_DUYET" ? "" : "CHO_DUYET";
    } else if (cardKey === "approved") {
      newTrangThai = appliedFilters.trangThai === "DA_DUYET" ? "" : "DA_DUYET";
    } else if (cardKey === "aiLearned") {
      newAiDaHoc = appliedFilters.aiDaHoc === true ? undefined : true;
    } else if (cardKey === "total") {
      newTrangThai = "";
      newAiDaHoc = undefined;
    }

    const newFilters = {
      ...appliedFilters,
      trangThai: newTrangThai,
      aiDaHoc: newAiDaHoc,
    };

    setAppliedFilters(newFilters);
    setDraftFilters((prev) => ({
      ...prev,
      trangThai: newTrangThai,
      aiDaHoc: newAiDaHoc,
    }));

    fetchDocuments(1, 10, {
      ...newFilters,
      search,
    });
  };

  const handleResetAllFilters = () => {
    const empty = {
      trangThai: "",
      phamVi: "",
      danhMuc: "",
      agency: "",
      kyBaoCao: "",
      fromDate: "",
      toDate: "",
      aiDaHoc: undefined,
    };
    setSearch("");
    setAppliedFilters(empty);
    setDraftFilters(empty);
    setShowFilterDropdown(false);
    fetchDocuments(1, 10, {
      search: "",
      ...empty,
    });
  };

  const handleExportExcel = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const params = {};
      if (search && search.trim()) params.search = search.trim();
      if (appliedFilters.trangThai) params.trangThai = appliedFilters.trangThai;
      if (appliedFilters.phamVi) params.phamVi = appliedFilters.phamVi;
      if (appliedFilters.danhMuc) params.idDanhMuc = appliedFilters.danhMuc;
      if (appliedFilters.agency) params.coQuanBanHanh = appliedFilters.agency;
      if (appliedFilters.fromDate) params.dateFrom = appliedFilters.fromDate;
      if (appliedFilters.toDate) params.dateTo = appliedFilters.toDate;

      // Ánh xạ chính xác danh sách cột trên UI sang key Backend yêu cầu
      const columnKeyMap = {
        // Module Văn hóa - Lịch sử
        tieu_de: "tieuDe",
        di_tich: "tenDiTich",
        ten_di_tich: "tenDiTich",
        phan_nhom: "danhMuc",
        danh_muc: "danhMuc",

        // Module Quy phạm pháp luật
        so_hieu: "soHieu",
        co_quan_ban_hanh: "coQuanBanHanh",
        loai_van_ban: "danhMuc",

        // Các cột dùng chung
        ngay_ban_hanh: "ngayBanHanh",
        pham_vi: "phamVi",
        ai_da_hoc: "aiDaHoc",
        trang_thai: "trangThai",
        ten_nguoi_duyet: "nguoiDuyet",
        nguoi_duyet: "nguoiDuyet",

        // Các trường mở rộng khác
        dia_chi: "diaChi",
        ngay_hieu_luc: "ngayHieuLuc",
        ngay_het_han: "ngayHetHan",
        trang_thai_hieu_luc: "trangThaiHieuLuc",
        luot_xem: "luotXem",
        luot_tai: "luotTai",
        nguoi_tao: "nguoiTao",
        thoi_gian_tao: "thoiGianTao",
      };

      const currentVisibleCols = (activeTab === "history" ? visibleHistoryCols : visibleLegalCols)
        .filter((k) => k !== "actions")
        .map((k) => columnKeyMap[k] || k)
        .filter(Boolean);

      if (currentVisibleCols.length > 0) {
        params.columns = JSON.stringify(currentVisibleCols);
      }

      let result;
      let filename = "";
      const nowStr = new Date().toISOString().slice(0, 10);
      if (activeTab === "history") {
        result = await exportHistoryDocuments(params);
        filename = `danh_sach_tai_lieu_van_hoa_${nowStr}.xlsx`;
      } else {
        result = await exportLegalDocuments(params);
        filename = `danh_sach_tai_lieu_phap_luat_${nowStr}.xlsx`;
      }

      if (result.success && result.data) {
        const blobUrl = window.URL.createObjectURL(
          new Blob([result.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        showToast.success("Xuất file Excel thành công!");
      } else {
        showToast.error(result.error || "Xuất file Excel thất bại!");
      }
    } catch (err) {
      console.error("Export Excel error:", err);
      showToast.error("Có lỗi xảy ra khi xuất file Excel");
    } finally {
      setIsExporting(false);
    }
  };

  const handleCreateSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      let result;
      if (activeTab === "history") {
        result = await createHistoryDocument(formData);
      } else {
        result = await createLegalDocument(formData);
      }

      if (result.success) {
        showToast.success("Tạo tài liệu thành công!");
        setFormMode(null);
        fetchDocuments(pagination?.currentPage || 1);
        if (activeTab === "history") {
          fetchHistoryStats();
        } else {
          fetchLegalStats();
        }
      } else {
        showToast.error(result.error || "Tạo tài liệu thất bại");
      }
    } catch (error) {
      showToast.error("Tạo tài liệu thất bại");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleView = async (item) => {
    setLoadingDetail(true);
    setIsDetailModalOpen(true);
    let detail;
    if (activeTab === "history") {
      detail = await getHistoryDocumentDetail(item.id);
    } else {
      detail = await getLegalDocumentDetail(item.id);
    }

    if (detail) {
      setDetailDocument(detail);
    } else {
      showToast.error("Không thể tải chi tiết tài liệu");
      setIsDetailModalOpen(false);
    }
    setLoadingDetail(false);
  };

  const handleEdit = async (item) => {
    if (item.trang_thai === "DA_DUYET") {
      showToast.warning("Tài liệu đã duyệt không thể chỉnh sửa.");
      return;
    }
    setLoadingDetail(true);
    let detail;
    if (activeTab === "history") {
      detail = await getHistoryDocumentDetail(item.id);
    } else {
      detail = await getLegalDocumentDetail(item.id);
    }
    setLoadingDetail(false);

    if (detail) {
      setEditDocument(detail);
      setFormMode("edit");
    } else {
      showToast.error("Không thể tải thông tin tài liệu");
    }
  };

  const handleEditSubmit = async (formData) => {
    if (!editDocument?.id) return;
    setIsEditSubmitting(true);
    try {
      let result;
      if (activeTab === "history") {
        result = await updateHistoryDocument(editDocument.id, formData);
      } else {
        result = await updateLegalDocument(editDocument.id, formData);
      }

      if (result.success) {
        showToast.success("Cập nhật tài liệu thành công!");
        const updatedData = result.data;
        setFormMode(null);
        setEditDocument(null);

        // Cập nhật duy nhất dòng được sửa trong state để giữ nguyên bảng, không render lại toàn bộ
        setDocuments((prevDocs) =>
          prevDocs.map((doc) => {
            if (doc.id === editDocument.id) {
              return {
                ...doc,
                ...(updatedData || {}),
                tieu_de: updatedData?.tieu_de || formData.get("tieuDe") || doc.tieu_de,
                so_hieu: updatedData?.so_hieu || formData.get("soHieu") || doc.so_hieu,
                so_ky_hieu: updatedData?.so_ky_hieu || formData.get("soHieu") || doc.so_ky_hieu,
                dia_chi: updatedData?.dia_chi || formData.get("diaChi") || doc.dia_chi,
                ten_di_tich: updatedData?.ten_di_tich || formData.get("tenDiTich") || doc.ten_di_tich,
                co_quan_ban_hanh: updatedData?.co_quan_ban_hanh || formData.get("coQuanBanHanh") || doc.co_quan_ban_hanh,
                ngay_ban_hanh: updatedData?.ngay_ban_hanh || formData.get("ngayBanHanh") || doc.ngay_ban_hanh,
                pham_vi: updatedData?.pham_vi || formData.get("phamVi") || doc.pham_vi,
                mo_ta: updatedData?.mo_ta || formData.get("moTa") || doc.mo_ta,
                tags: updatedData?.tags || formData.get("tags") || doc.tags,
                thu_vien_danh_muc: updatedData?.thu_vien_danh_muc || doc.thu_vien_danh_muc,
              };
            }
            return doc;
          })
        );

        // Đồng bộ dữ liệu ngầm mà không hiển thị skeleton/loading toàn bảng
        fetchDocuments(pagination?.currentPage || 1, 10, {}, true);
      } else {
        showToast.error(result.error || "Cập nhật tài liệu thất bại");
      }
    } catch (error) {
      showToast.error("Cập nhật tài liệu thất bại");
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteDocument(item);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async (lyDoXoa) => {
    if (!deleteDocument?.id) return;
    setIsDeleting(true);
    try {
      let result;
      if (activeTab === "history") {
        result = await deleteHistoryDocument(deleteDocument.id, lyDoXoa);
      } else {
        result = await deleteLegalDocument(deleteDocument.id, lyDoXoa);
      }

      if (result.success) {
        showToast.success("Xóa tài liệu thành công!");
        setIsDeleteConfirmOpen(false);
        setDeleteDocument(null);
        setDocuments((prevDocs) => prevDocs.filter((doc) => doc.id !== deleteDocument.id));
        fetchDocuments(pagination?.currentPage || 1, 10, {}, true);
        if (activeTab === "history") {
          fetchHistoryStats();
        } else {
          fetchLegalStats();
        }
      } else {
        showToast.error(result.error || "Xóa tài liệu thất bại");
      }
    } catch (error) {
      showToast.error("Xóa tài liệu thất bại");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBack = () => {
    setFormMode(null);
    setEditDocument(null);
  };

  const handleApprove = async (item) => {
    setApprovingId(item.id);
    try {
      let result;
      if (activeTab === "history") {
        result = await approveHistoryDocument(item.id);
      } else {
        result = await approveLegalDocument(item.id);
      }

      if (result.success) {
        showToast.success("Duyệt tài liệu thành công!");
        setDocuments((prevDocs) =>
          prevDocs.map((doc) =>
            doc.id === item.id ? { ...doc, trang_thai: "DA_DUYET" } : doc
          )
        );
        fetchDocuments(pagination?.currentPage || 1, 10, {}, true);
        if (activeTab === "history") fetchHistoryStats(true);
        else fetchLegalStats(true);
      } else {
        showToast.error(result.error || "Duyệt tài liệu thất bại");
      }
    } catch (error) {
      showToast.error("Duyệt tài liệu thất bại");
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectClick = (item) => {
    setRejectDocument(item);
    setIsRejectModalOpen(true);
  };

  const handleRejectConfirm = async (lyDoTuChoi) => {
    if (!rejectDocument) return;
    setIsRejecting(true);
    try {
      let result;
      if (activeTab === "history") {
        result = await rejectHistoryDocument(rejectDocument.id, lyDoTuChoi);
      } else {
        result = await rejectLegalDocument(rejectDocument.id, lyDoTuChoi);
      }

      if (result.success) {
        showToast.success("Từ chối tài liệu thành công!");
        setIsRejectModalOpen(false);
        setRejectDocument(null);
        setDocuments((prevDocs) =>
          prevDocs.map((doc) =>
            doc.id === rejectDocument.id ? { ...doc, trang_thai: "TU_CHOI", ly_do_tu_choi: lyDoTuChoi } : doc
          )
        );
        fetchDocuments(pagination?.currentPage || 1, 10, {}, true);
        if (activeTab === "history") fetchHistoryStats(true);
        else fetchLegalStats(true);
      } else {
        showToast.error(result.error || "Từ chối tài liệu thất bại");
      }
    } catch (error) {
      showToast.error("Từ chối tài liệu thất bại");
    } finally {
      setIsRejecting(false);
    }
  };

  const handleUnapproveClick = (item) => {
    setUnapproveDocument(item);
    setIsUnapproveConfirmOpen(true);
  };

  const handleUnapproveConfirm = async () => {
    if (!unapproveDocument?.id) return;
    setIsUnapproving(true);
    try {
      let result;
      if (activeTab === "history") {
        result = await unapproveHistoryDocument(unapproveDocument.id);
      } else {
        result = await unapproveLegalDocument(unapproveDocument.id);
      }

      if (result.success) {
        showToast.success("Hoàn tác phê duyệt thành công!");
        setIsUnapproveConfirmOpen(false);
        setUnapproveDocument(null);
        setDocuments((prevDocs) =>
          prevDocs.map((doc) =>
            doc.id === unapproveDocument.id ? { ...doc, trang_thai: "CHO_DUYET" } : doc
          )
        );
        fetchDocuments(pagination?.currentPage || 1, 10, {}, true);
        if (activeTab === "history") fetchHistoryStats(true);
        else fetchLegalStats(true);
      } else {
        showToast.error(result.error || "Hoàn tác phê duyệt thất bại");
      }
    } catch (error) {
      showToast.error("Hoàn tác phê duyệt thất bại");
    } finally {
      setIsUnapproving(false);
    }
  };

  const handleSubmitForApproval = async (item) => {
    try {
      let result;
      if (activeTab === "history") {
        result = await updateHistoryDocumentStatus(item.id, "CHO_DUYET");
      } else {
        result = await updateLegalDocumentStatus(item.id, "CHO_DUYET");
      }

      if (result.success) {
        showToast.success("Chuyển trạng thái sang chờ duyệt thành công!");
        setDocuments((prevDocs) =>
          prevDocs.map((doc) =>
            doc.id === item.id ? { ...doc, trang_thai: "CHO_DUYET" } : doc
          )
        );
        fetchDocuments(pagination?.currentPage || 1, 10, {}, true);
        if (activeTab === "history") fetchHistoryStats(true);
        else fetchLegalStats(true);
      } else {
        showToast.error(result.error || "Chuyển trạng thái thất bại");
      }
    } catch (error) {
      showToast.error("Chuyển trạng thái thất bại");
    }
  };

  const handleMoveToDraft = async (item) => {
    if (item?.trang_thai === "DA_DUYET") {
      showToast.error("Tài liệu đã duyệt không thể chuyển sang nháp. Vui lòng hoàn tác duyệt trước!");
      return;
    }
    try {
      let result;
      if (activeTab === "history") {
        result = await updateHistoryDocumentStatus(item.id, "NHAP");
      } else {
        result = await updateLegalDocumentStatus(item.id, "NHAP");
      }

      if (result.success) {
        showToast.success("Chuyển sang bản nháp thành công!");
        setDocuments((prevDocs) =>
          prevDocs.map((doc) =>
            doc.id === item.id ? { ...doc, trang_thai: "NHAP" } : doc
          )
        );
        fetchDocuments(pagination?.currentPage || 1, 10, {}, true);
        if (activeTab === "history") fetchHistoryStats(true);
        else fetchLegalStats(true);
      } else {
        showToast.error(result.error || "Chuyển sang bản nháp thất bại");
      }
    } catch (error) {
      showToast.error("Chuyển sang bản nháp thất bại");
    }
  };

  const handleToggleAiLearn = async (item) => {
    try {
      const action = item.ai_da_hoc ? "unlearn" : "learn";
      const result =
        activeTab === "history"
          ? await aiLearnHistoryDocument(item.id, action)
          : await aiLearnLegalDocument(item.id, action);

      if (result.success) {
        showToast.success(item.ai_da_hoc ? "Đã hủy đồng bộ AI" : "Đã đồng bộ cho AI học");
        setDocuments((prevDocs) =>
          prevDocs.map((doc) =>
            doc.id === item.id ? { ...doc, ai_da_hoc: !item.ai_da_hoc } : doc
          )
        );
        fetchDocuments(pagination?.currentPage || 1, 10, {}, true);
        if (activeTab === "history") {
          fetchHistoryStats(true);
        } else {
          fetchLegalStats(true);
        }
      } else {
        showToast.error(result.error || "Thao tác thất bại");
      }
    } catch (error) {
      showToast.error("Thao tác thất bại");
    }
  };

  /* ─── Render Action Cell (Shared for both History & Legal) ─── */
  const renderActionCell = (record) => {
    const isOpen = openActionMenuId === record.id;
    const isApproved = record.trang_thai === "DA_DUYET";
    const isRejected = record.trang_thai === "TU_CHOI";
    const isDraft = record.trang_thai === "NHAP";
    const isPending = record.trang_thai === "CHO_DUYET";
    const isApproving = approvingId === record.id;
    const canClickApprove = canApproveDocument && isPending && !isApproving;

    return (
      <div className={`flex items-center gap-2 relative ${isOpen ? "z-50" : ""}`}>
        {/* Nút Duyệt / Đã duyệt: Chỉ cho phép ấn khi ở trạng thái Chờ duyệt (CHO_DUYET) */}
        {canApproveDocument && (
          <button
            type="button"
            onClick={() => canClickApprove && handleApprove(record)}
            disabled={!canClickApprove}
            className={`w-[84px] py-1.5 text-xs font-medium rounded-md shadow-xs transition-all inline-flex items-center justify-center gap-1.5 ${
              isApproved
                ? "bg-blue-600 text-white opacity-40 cursor-not-allowed"
                : isRejected
                  ? "bg-blue-600 text-white opacity-40 cursor-not-allowed"
                  : isDraft
                    ? "bg-blue-600 text-white opacity-40 cursor-not-allowed"
                    : isApproving
                      ? "bg-blue-600 text-white opacity-70 cursor-wait"
                      : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
            }`}
            title={
              isApproved
                ? "Tài liệu đã được duyệt"
                : isRejected
                  ? "Tài liệu đã bị từ chối"
                  : isDraft
                    ? "Tài liệu đang ở trạng thái Nháp (cần Gửi duyệt trước)"
                    : isApproving
                      ? "Đang duyệt..."
                      : "Duyệt tài liệu"
            }
          >
            {isApproving ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Duyệt</span>
              </>
            ) : isApproved ? (
              "Đã duyệt"
            ) : (
              "Duyệt"
            )}
          </button>
        )}

        <div>
          <button
            type="button"
            onClick={(e) => handleOpenActionMenu(e, record)}
            className={`p-1.5 rounded-lg transition-colors ${
              isOpen ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            }`}
            title="Thao tác khác"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  /* ─── Columns for History Documents ─── */
  const HISTORY_COLUMNS = [
    {
      title: "Tên tài liệu",
      key: "tieu_de",
      dataIndex: "tieu_de",
      width: "250px",
      render: (value) => (
        <div
          className="text-sm text-gray-900 font-medium max-w-[250px] truncate"
          title={value}
        >
          {value || "---"}
        </div>
      ),
    },
    {
      title: "Di tích / Địa danh",
      key: "di_tich",
      width: "200px",
      render: (_, record) => {
        const parts = [record.ten_di_tich, record.dia_chi].filter(Boolean);
        return (
          <div
            className="text-sm text-gray-600 max-w-[200px]"
            title={parts.join(", ")}
          >
            <span className="truncate block">{parts.join(", ") || "---"}</span>
          </div>
        );
      },
    },
    {
      title: "Loại văn bản",
      key: "phan_nhom",
      width: "140px",
      render: (_, record) => (
        <span className="text-sm text-gray-600">
          {record.thu_vien_danh_muc?.ten || "---"}
        </span>
      ),
    },
    {
      title: "Ngày ban hành",
      key: "ngay_ban_hanh",
      dataIndex: "ngay_ban_hanh",
      width: "130px",
      render: (value) => (
        <span className="text-sm text-gray-600">
          {value ? formatDateShort(value) : "---"}
        </span>
      ),
    },
    {
      title: "Phạm vi",
      key: "pham_vi",
      dataIndex: "pham_vi",
      width: "100px",
      render: (value) => {
        const map = {
          CONG_KHAI: {
            label: "Công khai",
            bg: "bg-green-100",
            text: "text-green-800",
          },
          NOI_BO: { label: "Nội bộ", bg: "bg-blue-100", text: "text-blue-800" },
          HAN_CHE: { label: "Hạn chế", bg: "bg-red-100", text: "text-red-800" },
        };
        const s = map[value] || {
          label: value || "---",
          bg: "bg-gray-100",
          text: "text-gray-800",
        };
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}
          >
            {s.label}
          </span>
        );
      },
    },
    {
      title: "Trợ lý AI",
      key: "ai_da_hoc",
      dataIndex: "ai_da_hoc",
      width: "100px",
      render: (value) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            value
              ? "bg-purple-100 text-purple-800"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {value ? "Đã học" : "Chưa học"}
        </span>
      ),
    },
    {
      title: "Trạng thái",
      key: "trang_thai",
      dataIndex: "trang_thai",
      width: "110px",
      render: (value) => {
        const statusMap = {
          NHAP: { label: "Nháp", bg: "bg-gray-100", text: "text-gray-800" },
          CHO_DUYET: {
            label: "Chờ duyệt",
            bg: "bg-yellow-100",
            text: "text-yellow-800",
          },
          DA_DUYET: {
            label: "Đã duyệt",
            bg: "bg-green-100",
            text: "text-green-800",
          },
          TU_CHOI: {
            label: "Từ chối",
            bg: "bg-red-100",
            text: "text-red-700 font-medium",
          },
          LUU_TRU: {
            label: "Lưu trữ",
            bg: "bg-blue-100",
            text: "text-blue-800",
          },
        };
        const s = statusMap[value] || {
          label: value || "---",
          bg: "bg-gray-100",
          text: "text-gray-800",
        };
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}
          >
            {s.label}
          </span>
        );
      },
    },
    ...(canApproveDocument || canRejectDocument
      ? [
          {
            title: "Người tạo",
            key: "ten_nguoi_tao",
            dataIndex: "ten_nguoi_tao",
            width: "140px",
            render: (value, record) => (
              <span className="text-sm text-gray-600">
                {value || record.nguoi_tao_info?.ho_va_ten || "---"}
              </span>
            ),
          },
        ]
      : []),
    {
      title: "Người duyệt",
      key: "ten_nguoi_duyet",
      dataIndex: "ten_nguoi_duyet",
      width: "150px",
      render: (value) => (
        <span className="text-sm text-gray-600">{value || "---"}</span>
      ),
    },
    {
      title: "Thao tác",
      key: "actions",
      width: canApproveDocument ? "150px" : "80px",
      className: (record) => (openActionMenuId === record.id ? "!z-40" : "!z-10"),
      render: (_, record) => renderActionCell(record),
    },
  ];

  /* ─── Columns for Legal Documents ─── */
  const LEGAL_COLUMNS = [
    {
      title: "Số hiệu",
      key: "so_hieu",
      dataIndex: "so_hieu",
      width: "160px",
      render: (value) => (
        <span className="text-sm font-semibold text-blue-700">{value || "---"}</span>
      ),
    },
    {
      title: "Tiêu đề văn bản",
      key: "tieu_de",
      dataIndex: "tieu_de",
      width: "280px",
      render: (value) => (
        <div
          className="text-sm text-gray-900 font-medium max-w-[280px] line-clamp-2"
          title={value}
        >
          {value || "---"}
        </div>
      ),
    },
    {
      title: "Cơ quan ban hành",
      key: "co_quan_ban_hanh",
      dataIndex: "co_quan_ban_hanh",
      width: "180px",
      render: (value) => (
        <span className="text-sm text-gray-700 block max-w-[180px] truncate" title={value}>
          {value || "---"}
        </span>
      ),
    },
    {
      title: "Loại văn bản",
      key: "loai_van_ban",
      width: "130px",
      render: (_, record) => (
        <span className="text-sm text-gray-600">
          {record.thu_vien_danh_muc?.ten || "---"}
        </span>
      ),
    },
    {
      title: "Ngày ban hành",
      key: "ngay_ban_hanh",
      dataIndex: "ngay_ban_hanh",
      width: "130px",
      render: (value) => (
        <span className="text-sm text-gray-600">
          {value ? formatDateShort(value) : "---"}
        </span>
      ),
    },
    {
      title: "Phạm vi",
      key: "pham_vi",
      dataIndex: "pham_vi",
      width: "100px",
      render: (value) => {
        const map = {
          CONG_KHAI: { label: "Công khai", bg: "bg-green-100", text: "text-green-800" },
          NOI_BO: { label: "Nội bộ", bg: "bg-blue-100", text: "text-blue-800" },
          HAN_CHE: { label: "Hạn chế", bg: "bg-red-100", text: "text-red-800" },
        };
        const s = map[value] || { label: value || "---", bg: "bg-gray-100", text: "text-gray-800" };
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
            {s.label}
          </span>
        );
      },
    },
    {
      title: "Trợ lý AI",
      key: "ai_da_hoc",
      dataIndex: "ai_da_hoc",
      width: "100px",
      render: (value) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            value ? "bg-purple-100 text-purple-800" : "bg-gray-100 text-gray-500"
          }`}
        >
          {value ? "Đã học" : "Chưa học"}
        </span>
      ),
    },
    {
      title: "Trạng thái",
      key: "trang_thai",
      dataIndex: "trang_thai",
      width: "110px",
      render: (value) => {
        const statusMap = {
          NHAP: { label: "Nháp", bg: "bg-gray-100", text: "text-gray-800" },
          CHO_DUYET: { label: "Chờ duyệt", bg: "bg-yellow-100", text: "text-yellow-800" },
          DA_DUYET: { label: "Đã duyệt", bg: "bg-green-100", text: "text-green-800" },
          TU_CHOI: { label: "Từ chối", bg: "bg-red-100", text: "text-red-700 font-medium" },
          LUU_TRU: { label: "Lưu trữ", bg: "bg-blue-100", text: "text-blue-800" },
        };
        const s = statusMap[value] || { label: value || "---", bg: "bg-gray-100", text: "text-gray-800" };
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
            {s.label}
          </span>
        );
      },
    },
    ...(canApproveDocument || canRejectDocument
      ? [
          {
            title: "Người tạo",
            key: "ten_nguoi_tao",
            dataIndex: "ten_nguoi_tao",
            width: "140px",
            render: (value, record) => (
              <span className="text-sm text-gray-600">
                {value || record.nguoi_tao_info?.ho_va_ten || "---"}
              </span>
            ),
          },
        ]
      : []),
    {
      title: "Người duyệt",
      key: "ten_nguoi_duyet",
      dataIndex: "ten_nguoi_duyet",
      width: "140px",
      render: (value) => <span className="text-sm text-gray-600">{value || "---"}</span>,
    },
    {
      title: "Thao tác",
      key: "actions",
      width: canApproveDocument ? "150px" : "80px",
      className: (record) => (openActionMenuId === record.id ? "!z-40" : "!z-10"),
      render: (_, record) => renderActionCell(record),
    },
  ];

  const currentColumns = activeTab === "history" ? HISTORY_COLUMNS : LEGAL_COLUMNS;
  const currentVisibleKeys = activeTab === "history" ? visibleHistoryCols : visibleLegalCols;
  const displayColumns = currentColumns.filter(
    (col) => col.key === "actions" || currentVisibleKeys.includes(col.key)
  );

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý tài liệu</h1>
          <p className="text-gray-600 mt-1">
            {activeTab === "history"
              ? "Quản lý tài liệu văn hóa - lịch sử địa phương"
              : "Quản lý văn bản quy phạm pháp luật và văn bản chỉ đạo điều hành"}
          </p>
        </div>
        {!formMode && (
          <button
            type="button"
            onClick={() => setFormMode("create")}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            Tài liệu mới
          </button>
        )}
      </div>

      {/* Form Mode for History */}
      {activeTab === "history" && formMode && (
        <DocumentFormModal
          onBack={handleBack}
          onSubmit={formMode === "edit" ? handleEditSubmit : handleCreateSubmit}
          onSaveDraft={formMode === "edit" ? handleEditSubmit : handleCreateSubmit}
          subCategories={subCategories}
          isLoading={formMode === "edit" ? isEditSubmitting : isSubmitting}
          initialData={formMode === "edit" ? editDocument : null}
          isEditMode={formMode === "edit"}
        />
      )}

      {/* Form Mode for Legal */}
      {activeTab === "legal" && formMode && (
        <LegalDocumentFormModal
          onBack={handleBack}
          onSubmit={formMode === "edit" ? handleEditSubmit : handleCreateSubmit}
          onSaveDraft={formMode === "edit" ? handleEditSubmit : handleCreateSubmit}
          docTypes={docTypes}
          issuingAgencies={issuingAgencies}
          isLoading={formMode === "edit" ? isEditSubmitting : isSubmitting}
          initialData={formMode === "edit" ? editDocument : null}
          isEditMode={formMode === "edit"}
        />
      )}

      {/* Main List Views */}
      {!formMode && (
        <div>
          {/* Stat Cards */}
          {loadingStats ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="bg-gray-50 rounded-lg p-4 animate-pulse h-24"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {STAT_CARDS.map((card) => {
                const Icon = card.icon;
                const colors = COLOR_MAP[card.color];
                const value = stats?.[card.key] ?? 0;

                return (
                  <div
                    role="button"
                    tabIndex={0}
                    key={card.key}
                    onClick={() => handleStatCardClick(card.key)}
                    onKeyDown={(e) => e.key === "Enter" && handleStatCardClick(card.key)}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer select-none"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 font-medium">
                          {card.label}
                        </p>
                        <p className={`text-4xl font-bold mt-1 ${colors.text}`}>
                          {value.toLocaleString()}
                        </p>
                      </div>
                      <div className={`p-3 rounded-full ${colors.bg}`}>
                        <Icon className={`w-6 h-6 ${colors.icon}`} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Search & Filter Bar */}
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 mb-4 flex items-center justify-between gap-3 flex-wrap">
            <form
              onSubmit={handleSearch}
              className="flex items-center gap-2.5 flex-1 min-w-0 flex-wrap"
            >
              <div className="relative flex-1 max-w-md min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={
                    activeTab === "history"
                      ? "Tìm kiếm tài liệu..."
                      : "Tìm theo số hiệu, tiêu đề, mô tả..."
                  }
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Nút dropdown gom các bộ lọc */}
              <div className="relative" ref={filterDropdownRef}>
                <button
                  type="button"
                  onClick={handleToggleFilterDropdown}
                  className={`px-3 py-2 border rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    showFilterDropdown || activeFilterCount > 0
                      ? "bg-blue-50 border-blue-400 text-blue-700 shadow-xs"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                  title="Bộ lọc nâng cao"
                >
                  <Filter className={`w-4 h-4 ${activeFilterCount > 0 ? "text-blue-600" : "text-gray-500"}`} />
                  <span>Bộ lọc</span>
                  {activeFilterCount > 0 && (
                    <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showFilterDropdown ? "rotate-180" : ""}`} />
                </button>

                {showFilterDropdown && (
                  <div className="absolute left-0 top-full mt-2 w-[340px] sm:w-[480px] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 animate-in fade-in zoom-in-95 duration-150 overflow-visible">
                    {/* Body: Form rows label - control */}
                    <div className="p-4 space-y-3.5">
                      {/* Loại văn bản cho Văn hóa */}
                      {activeTab === "history" && subCategories.length > 0 && (
                        <div className="flex items-center gap-3">
                          <label className="w-28 sm:w-32 flex-shrink-0 text-sm font-medium text-gray-700">
                            Loại văn bản
                          </label>
                          <div className="flex-1 min-w-0">
                            <CustomSelect
                              value={draftFilters.danhMuc}
                              onChange={(val) =>
                                setDraftFilters((prev) => ({ ...prev, danhMuc: val }))
                              }
                              options={[
                                { value: "", label: "Tất cả loại văn bản" },
                                ...subCategories.map((t) => ({
                                  value: t.id,
                                  label: `${t.name} (${t.documentCount ?? 0})`,
                                })),
                              ]}
                              placeholder="Tất cả phân nhóm"
                              className="w-full"
                            />
                          </div>
                        </div>
                      )}

                      {/* Loại văn bản cho Pháp luật */}
                      {activeTab === "legal" && docTypes.length > 0 && (
                        <div className="flex items-center gap-3">
                          <label className="w-28 sm:w-32 flex-shrink-0 text-sm font-medium text-gray-700">
                            Loại văn bản
                          </label>
                          <div className="flex-1 min-w-0">
                            <CustomSelect
                              value={draftFilters.danhMuc}
                              onChange={(val) =>
                                setDraftFilters((prev) => ({ ...prev, danhMuc: val }))
                              }
                              options={[
                                { value: "", label: "Tất cả loại văn bản" },
                                ...docTypes.map((t) => ({
                                  value: t.id,
                                  label: `${t.name} (${t.documentCount ?? 0})`,
                                })),
                              ]}
                              placeholder="Tất cả loại văn bản"
                              className="w-full"
                            />
                          </div>
                        </div>
                      )}

                      {/* Cơ quan ban hành cho Pháp luật */}
                      {activeTab === "legal" && issuingAgencies.length > 0 && (
                        <div className="flex items-center gap-3">
                          <label className="w-28 sm:w-32 flex-shrink-0 text-sm font-medium text-gray-700">
                            Cơ quan ban hành
                          </label>
                          <div className="flex-1 min-w-0">
                            <CustomSelect
                              value={draftFilters.agency}
                              onChange={(val) =>
                                setDraftFilters((prev) => ({ ...prev, agency: val }))
                              }
                              options={[
                                { value: "", label: "Tất cả cơ quan" },
                                ...issuingAgencies.map((a) => ({
                                  value: a.name,
                                  label: `${a.name} (${a.documentCount ?? 0})`,
                                })),
                              ]}
                              placeholder="Tất cả cơ quan"
                              className="w-full"
                            />
                          </div>
                        </div>
                      )}

                      {/* Trạng thái */}
                      <div className="flex items-center gap-3">
                        <label className="w-28 sm:w-32 flex-shrink-0 text-sm font-medium text-gray-700">
                          Trạng thái
                        </label>
                        <div className="flex-1 min-w-0">
                          <CustomSelect
                            value={draftFilters.trangThai}
                            onChange={(val) =>
                              setDraftFilters((prev) => ({ ...prev, trangThai: val }))
                            }
                            options={[
                              { value: "", label: "Tất cả trạng thái" },
                              { value: "TU_CHOI", label: "Từ chối" },
                              { value: "CHO_DUYET", label: "Chờ duyệt" },
                              { value: "DA_DUYET", label: "Đã duyệt" },
                              { value: "NHAP", label: "Nháp" },
                            ]}
                            placeholder="Tất cả trạng thái"
                            className="w-full"
                          />
                        </div>
                      </div>

                      {/* Phạm vi */}
                      <div className="flex items-center gap-3">
                        <label className="w-28 sm:w-32 flex-shrink-0 text-sm font-medium text-gray-700">
                          Phạm vi
                        </label>
                        <div className="flex-1 min-w-0">
                          <CustomSelect
                            value={draftFilters.phamVi}
                            onChange={(val) =>
                              setDraftFilters((prev) => ({ ...prev, phamVi: val }))
                            }
                            options={[
                              { value: "", label: "Tất cả phạm vi" },
                              { value: "CONG_KHAI", label: "Công khai" },
                              { value: "NOI_BO", label: "Nội bộ" },
                            ]}
                            placeholder="Tất cả phạm vi"
                            className="w-full"
                          />
                        </div>
                      </div>

                      {/* Kỳ báo cáo */}
                      <div className="flex items-center gap-3">
                        <label className="w-28 sm:w-32 flex-shrink-0 text-sm font-medium text-gray-700">
                          Kỳ báo cáo
                        </label>
                        <div className="flex-1 min-w-0">
                          <CustomSelect
                            value={draftFilters.kyBaoCao}
                            onChange={handlePeriodChange}
                            options={REPORT_PERIOD_OPTIONS}
                            placeholder="Tất cả thời gian / Tùy chọn"
                            className="w-full"
                          />
                        </div>
                      </div>

                      {/* Lọc theo ngày ban hành */}
                      <div className="flex items-center gap-3">
                        <label className="w-28 sm:w-32 flex-shrink-0 text-sm font-medium text-gray-700">
                          Từ ngày
                        </label>
                        <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap sm:flex-nowrap">
                          <div className="relative flex-1 min-w-[130px]">
                            <VietnameseDatePicker
                              value={draftFilters.fromDate}
                              maxDate={draftFilters.toDate || undefined}
                              onChange={(isoVal) =>
                                setDraftFilters((prev) => ({
                                  ...prev,
                                  fromDate: isoVal,
                                  kyBaoCao: "",
                                }))
                              }
                              placeholder="dd/MM/yyyy"
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700 whitespace-nowrap px-1">
                            Đến ngày
                          </span>
                          <div className="relative flex-1 min-w-[130px]">
                            <VietnameseDatePicker
                              value={draftFilters.toDate}
                              minDate={draftFilters.fromDate || undefined}
                              onChange={(isoVal) =>
                                setDraftFilters((prev) => ({
                                  ...prev,
                                  toDate: isoVal,
                                  kyBaoCao: "",
                                }))
                              }
                              placeholder="dd/MM/yyyy"
                            />
                          </div>
                          {(draftFilters.kyBaoCao || draftFilters.fromDate || draftFilters.toDate) && (
                            <button
                              type="button"
                              onClick={() => {
                                setDraftFilters((prev) => ({
                                  ...prev,
                                  kyBaoCao: "",
                                  fromDate: "",
                                  toDate: "",
                                }));
                              }}
                              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200"
                              title="Xóa lọc ngày"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 rounded-b-lg flex items-center justify-end gap-2.5">
                      <button
                        type="button"
                        onClick={handleApplyFilters}
                        className="inline-flex items-center justify-center gap-1.5 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md shadow-sm transition-colors cursor-pointer"
                      >
                        <Check className="w-4 h-4 stroke-[3]" />
                        <span>Đồng ý</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowFilterDropdown(false)}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-gray-700 hover:text-red-600 hover:bg-gray-200/60 text-sm font-medium rounded-md transition-colors cursor-pointer"
                      >
                        <X className="w-4 h-4" />
                        <span>Hủy bỏ</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex-shrink-0"
              >
                Tìm kiếm
              </button>

              {/* Nút đặt lại bộ lọc khi có bất kỳ điều kiện lọc nào */}
              {(search || activeFilterCount > 0) && (
                <button
                  type="button"
                  onClick={handleResetAllFilters}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 border border-gray-200 rounded-lg transition-colors flex items-center gap-1.5 font-medium flex-shrink-0 cursor-pointer"
                  title="Đặt lại bộ lọc"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Đặt lại</span>
                </button>
              )}
            </form>

            {/* Right Action buttons: Export Excel & Column Setting */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Nút Xuất Excel */}
              <button
                type="button"
                onClick={handleExportExcel}
                disabled={isExporting}
                className="px-3 py-2 bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-60 text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 shadow-xs cursor-pointer disabled:cursor-not-allowed"
                title="Xuất danh sách ra file Excel"
              >
                {isExporting ? (
                  <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Download className="w-4 h-4 text-gray-500" />
                )}
                <span className="hidden sm:inline">Xuất Excel</span>
              </button>

              {/* Column Visibility Customization Button & Popover */}
              <div className="relative flex-shrink-0" ref={columnSettingRef}>
                <button
                  type="button"
                  onClick={() => setShowColumnSetting((prev) => !prev)}
                  className={`p-2 border rounded-lg transition-all flex items-center justify-center ${
                    showColumnSetting
                      ? "bg-blue-50 border-blue-400 text-blue-600 shadow-xs"
                      : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                  title="Tùy chỉnh cột hiển thị"
                >
                  <Settings className={`w-5 h-5 ${showColumnSetting ? "text-blue-600" : "text-gray-600"}`} />
                </button>

              {showColumnSetting && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 p-3 z-50">
                  <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100">
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Cột hiển thị ({displayColumns.length}/{(activeTab === "history" ? HISTORY_COLUMNS : LEGAL_COLUMNS).length})
                    </span>
                    <button
                      type="button"
                      onClick={handleResetColumns}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline"
                    >
                      Mặc định
                    </button>
                  </div>

                  <div className="max-h-64 overflow-y-auto space-y-1 py-1">
                    {(activeTab === "history" ? HISTORY_COLUMNS : LEGAL_COLUMNS).map((col) => {
                      const isActions = col.key === "actions";
                      const currentVisibleKeys = activeTab === "history" ? visibleHistoryCols : visibleLegalCols;
                      const isChecked = isActions || currentVisibleKeys.includes(col.key);

                      return (
                        <label
                          key={col.key}
                          className={`flex items-center justify-between px-2 py-1.5 rounded-lg text-sm transition-colors select-none ${
                            isActions
                              ? "bg-gray-50 text-gray-400 cursor-not-allowed opacity-80"
                              : isChecked
                                ? "hover:bg-blue-50/60 text-gray-800 cursor-pointer"
                                : "hover:bg-gray-50 text-gray-500 cursor-pointer"
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              disabled={isActions}
                              onChange={() => !isActions && handleToggleColumn(col.key)}
                              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                            />
                            <span className="font-normal truncate">{col.title}</span>
                          </span>
                          {isActions && (
                            <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded font-medium">
                              Cố định
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

          {/* Table */}
          <BaseTable
            data={documents}
            columns={displayColumns}
            loading={loadingTable}
            pagination={pagination}
            onPageChange={handlePageChange}
            emptyMessage="Không có tài liệu nào"
            showActions={false}
          />

          {/* Bottom Action Bar: Tài liệu đã xóa */}
          <div className="mt-4 flex items-center justify-between pt-3 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsDeletedModalOpen(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-red-50 hover:text-red-700 hover:border-red-200 border border-gray-300 rounded-lg shadow-xs transition-all cursor-pointer group"
              title="Xem danh sách tài liệu đã xóa"
            >
              <Trash2 className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors" />
              <span>Tài liệu đã xóa</span>
            </button>
            <p className="text-xs text-gray-400">
              Danh mục lưu trữ tài liệu điện tử
            </p>
          </div>
        </div>
      )}

      {/* Deleted Documents Modal */}
      <DeletedDocumentsModal
        isOpen={isDeletedModalOpen}
        onClose={() => setIsDeletedModalOpen(false)}
        activeTab={activeTab}
        onRestored={() => {
          fetchDocuments(pagination?.currentPage || 1);
          if (activeTab === "history") {
            fetchHistoryStats();
          } else {
            fetchLegalStats();
          }
        }}
      />

      {/* History Detail Modal */}
      {activeTab === "history" && (
        <DocumentDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setDetailDocument(null);
          }}
          document={detailDocument}
          loading={loadingDetail}
        />
      )}

      {/* Legal Detail Modal */}
      {activeTab === "legal" && (
        <LegalDocumentDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setDetailDocument(null);
          }}
          document={detailDocument}
          loading={loadingDetail}
        />
      )}

      {/* Delete Confirm Modal */}
      <DeleteDocumentModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => {
          setIsDeleteConfirmOpen(false);
          setDeleteDocument(null);
        }}
        onConfirm={handleDeleteConfirm}
        document={deleteDocument}
        isOwnDoc={isCreatedByCurrentUser(deleteDocument)}
        loading={isDeleting}
      />

      {/* Reject Modal with Reason input */}
      <RejectDocumentModal
        isOpen={isRejectModalOpen}
        onClose={() => {
          setIsRejectModalOpen(false);
          setRejectDocument(null);
        }}
        onConfirm={handleRejectConfirm}
        document={rejectDocument}
        loading={isRejecting}
      />

      {/* Confirm Unapprove Modal */}
      <ConfirmModal
        isOpen={isUnapproveConfirmOpen}
        onClose={() => {
          if (!isUnapproving) {
            setIsUnapproveConfirmOpen(false);
            setUnapproveDocument(null);
          }
        }}
        onConfirm={handleUnapproveConfirm}
        title="Hoàn tác phê duyệt"
        message={`Bạn có chắc chắn muốn hoàn tác phê duyệt tài liệu "${unapproveDocument?.tieu_de || ""}"? Tài liệu sẽ quay về trạng thái Chờ duyệt.`}
        confirmText={isUnapproving ? "Đang hoàn tác..." : "Hoàn tác duyệt"}
        cancelText="Hủy"
        type="warning"
        confirmType="primary"
        loading={isUnapproving}
      />

      {/* Floating Action Menu Portal */}
      {openActionMenuId && activeMenuRecord && createPortal(
        <div
          ref={actionMenuRef}
          style={{
            position: "fixed",
            top: menuPosition.placement === "top" ? "auto" : `${menuPosition.top}px`,
            bottom: menuPosition.placement === "top" ? `${window.innerHeight - menuPosition.top}px` : "auto",
            left: `${menuPosition.left}px`,
            transform: "translateX(-100%)",
          }}
          className="w-48 bg-white rounded-xl shadow-2xl border border-gray-200 py-1.5 z-[9999] animate-in fade-in zoom-in-95 duration-100"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => {
              const rec = activeMenuRecord;
              setOpenActionMenuId(null);
              setActiveMenuRecord(null);
              handleView(rec);
            }}
            className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Xem chi tiết
          </button>

          {canRejectDocument && activeMenuRecord.trang_thai === "CHO_DUYET" && (
            <button
              type="button"
              onClick={() => {
                const rec = activeMenuRecord;
                setOpenActionMenuId(null);
                setActiveMenuRecord(null);
                handleRejectClick(rec);
              }}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <XCircle className="w-4 h-4 text-red-500" />
              Từ chối
            </button>
          )}

          {canUnapproveDocument && activeMenuRecord.trang_thai === "DA_DUYET" && (
            <button
              type="button"
              onClick={() => {
                const rec = activeMenuRecord;
                setOpenActionMenuId(null);
                setActiveMenuRecord(null);
                handleUnapproveClick(rec);
              }}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-amber-700 hover:bg-amber-50 transition-colors"
            >
              <RotateCcw className="w-4 h-4 text-amber-600" />
              Hoàn tác duyệt
            </button>
          )}

          {(activeMenuRecord.trang_thai === "NHAP" || activeMenuRecord.trang_thai === "TU_CHOI") && (
            <button
              type="button"
              onClick={() => {
                const rec = activeMenuRecord;
                setOpenActionMenuId(null);
                setActiveMenuRecord(null);
                handleSubmitForApproval(rec);
              }}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-amber-700 hover:bg-amber-50 transition-colors font-medium cursor-pointer"
            >
              <Send className="w-4 h-4 text-amber-600" />
              <span>Gửi duyệt</span>
            </button>
          )}

          {activeMenuRecord.trang_thai !== "NHAP" && activeMenuRecord.trang_thai !== "DA_DUYET" && (
            <button
              type="button"
              onClick={() => {
                const rec = activeMenuRecord;
                setOpenActionMenuId(null);
                setActiveMenuRecord(null);
                handleMoveToDraft(rec);
              }}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <FileEdit className="w-4 h-4 text-slate-500" />
              <span>Chuyển sang nháp</span>
            </button>
          )}



          {(() => {
            const isApproverOrRejecter = canApproveDocument || canRejectDocument;
            const isOwnDoc = isCreatedByCurrentUser(activeMenuRecord);
            const canShowEdit = activeMenuRecord.trang_thai !== "DA_DUYET";
            const canShowDelete = (() => {
              // BẮT BUỘC: Tài liệu ở trạng thái ĐÃ DUYỆT -> Ẩn nút Xóa (Phải Hoàn tác duyệt trước mới được xóa)
              if (activeMenuRecord.trang_thai === "DA_DUYET") {
                return false;
              }
              if (isOwnDoc) {
                return true;
              }
              if (canAdminDeleteDocument) {
                return activeMenuRecord.trang_thai === "CHO_DUYET" || activeMenuRecord.trang_thai === "NHAP";
              }
              return false;
            })();

            const showDivider = canShowEdit || canShowDelete;

            return (
              <>
                {showDivider && <div className="border-t border-gray-100 my-1" />}

                {canShowEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      const rec = activeMenuRecord;
                      setOpenActionMenuId(null);
                      setActiveMenuRecord(null);
                      handleEdit(rec);
                    }}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                    title="Chỉnh sửa tài liệu"
                  >
                    <svg
                      className="w-4 h-4 text-indigo-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    <span>Chỉnh sửa</span>
                  </button>
                )}

                {canShowDelete && (
                  <button
                    type="button"
                    onClick={() => {
                      const rec = activeMenuRecord;
                      setOpenActionMenuId(null);
                      setActiveMenuRecord(null);
                      handleDeleteClick(rec);
                    }}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                    title="Xóa tài liệu"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Xóa</span>
                  </button>
                )}
              </>
            );
          })()}
        </div>,
        document.body
      )}
    </div>
  );
}
